import React from 'react';
import {
  Stethoscope,
  HeartPulse,
  Apple,
  Activity,
  MessageSquare,
  FlaskConical,
  Sparkles,
} from 'lucide-react';

const features = [
  {
    id: 'doctor-guided',
    title: 'Doctor-Guided Wellness',
    description:
      'Programs designed with medical understanding of hormonal health, metabolism, recovery, and sustainable wellness.',
    icon: Stethoscope,
  },
  {
    id: 'specialized-health',
    title: 'Specialized Health Support',
    description:
      'Focused programs for weight, metabolic and lifestyle health, plus dedicated support for fertility, pregnancy, postpartum recovery, and PMOS (previously called PCOS).',
    icon: HeartPulse,
  },
  {
    id: 'personalized-nutrition',
    title: 'Personalized Nutrition',
    description:
      'Structured nutrition guidance tailored to individual health stages, goals, routines, and recovery needs.',
    icon: Apple,
  },
  {
    id: 'safe-medical-fitness',
    title: 'Safe Medical Fitness',
    description:
      'Fitness systems designed to prioritize safety, recovery, strength, mobility, and long-term consistency.',
    icon: Activity,
  },
  {
    id: 'continuous-support',
    title: 'Continuous Support',
    description:
      'Stay connected through digital monitoring, WhatsApp guidance, progress tracking, and ongoing accountability.',
    icon: MessageSquare,
  },
  {
    id: 'evidence-based',
    title: 'Evidence-Based Approach',
    description:
      'Clinically informed systems focused on long-term results, built around your real medical context.',
    icon: FlaskConical,
  },
];

const WhyTCubeSection = () => {
  return (
    <section
      id="why-tcube"
      className="relative py-20 sm:py-24 lg:py-28 bg-slate-950 overflow-hidden"
      aria-labelledby="why-tcube-heading"
      data-testid="why-tcube-section"
    >
      {/* Subtle ambient glow */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute top-0 left-1/3 w-[40rem] h-[40rem] bg-teal-500/[0.06] rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[32rem] h-[32rem] bg-cyan-500/[0.05] rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Intro */}
        <div className="max-w-3xl mx-auto text-center mb-14 sm:mb-16 lg:mb-20">
          <span
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-teal-400/10 border border-teal-400/30 text-teal-300 text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] mb-5"
            data-testid="why-tcube-eyebrow"
          >
            <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
            Why People Choose TCube Fitness
          </span>

          <h2
            id="why-tcube-heading"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight tracking-tight mb-5"
            data-testid="why-tcube-headline"
          >
            Built Around Real Health,{' '}
            <span className="text-teal-300">Not Just Weight Loss.</span>
          </h2>

          <p
            className="text-base sm:text-lg text-slate-300/90 leading-relaxed max-w-2xl mx-auto"
            data-testid="why-tcube-supporting"
          >
            TCube Fitness combines evidence-based nutrition, medically guided fitness, and
            personalized wellness systems designed for people of all ages and health goals.
          </p>
        </div>

        {/* Feature grid */}
        <ul
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto"
          data-testid="why-tcube-grid"
        >
          {features.map((feature) => {
            const Icon = feature.icon;
            return (
              <li
                key={feature.id}
                className="group relative rounded-3xl bg-white/[0.03] border border-teal-400/15 backdrop-blur-sm
                           p-7 sm:p-8
                           shadow-[0_1px_0_0_rgba(255,255,255,0.04)_inset,0_24px_60px_-30px_rgba(8,47,73,0.6)]
                           transition-all duration-300 ease-out
                           hover:border-teal-300/40 hover:bg-white/[0.05]
                           hover:shadow-[0_1px_0_0_rgba(255,255,255,0.06)_inset,0_30px_80px_-30px_rgba(20,184,166,0.35)]"
                data-testid={`why-card-${feature.id}`}
              >
                <div
                  className="w-12 h-12 rounded-2xl bg-teal-500/10 border border-teal-400/30 text-teal-300
                             flex items-center justify-center mb-6
                             transition-colors duration-300 group-hover:bg-teal-500/15 group-hover:text-teal-200"
                  aria-hidden="true"
                >
                  <Icon className="w-5 h-5" />
                </div>

                <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 leading-snug">
                  {feature.title}
                </h3>
                <p className="text-sm sm:text-base text-slate-300/85 leading-relaxed">
                  {feature.description}
                </p>
              </li>
            );
          })}
        </ul>

        {/* Bottom premium highlight */}
        <div className="mt-16 sm:mt-20 lg:mt-24 max-w-3xl mx-auto text-center" data-testid="why-tcube-highlight">
          <div
            className="rounded-3xl border border-teal-400/20 bg-gradient-to-b from-white/[0.04] to-white/[0.015]
                       backdrop-blur-sm px-7 py-10 sm:px-10 sm:py-14
                       shadow-[0_30px_80px_-40px_rgba(20,184,166,0.35)]"
          >
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight tracking-tight mb-4">
              Your Health Deserves More Than{' '}
              <span className="text-teal-300">Generic Fitness Advice.</span>
            </h3>
            <p className="text-base sm:text-lg text-slate-300/90 leading-relaxed max-w-xl mx-auto">
              TCube Fitness was built to provide structured, medically responsible wellness
              support that people of all ages can confidently follow through every stage of life.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyTCubeSection;
